import { createBrowserRouter, To } from 'react-router';
import { sessionStorage } from './storage';

export interface IHistoryInitParams {
  loginBaseUrl: string;
  logoutBaseUrl: string;
  signupBaseUrl?: string;
  BlogBaseUrl: string;
  OnboardingAppPath: string;
  TrainingBaseUrl: string;
  SocialAppPath: string;
  SubscribeUrl: string;
  getLanguageFunction: () => string;
}

export function createHistoryService() {
  let router: ReturnType<typeof createBrowserRouter>;
  let initialized = false;
  let windowObjectReference: Window | null = null;
  let previousURL: string;
  let listeners: Array<(path: string) => void> = [];

  let LoginBaseUrl: string;
  let LogoutBaseUrl: string;
  let SignupBaseUrl: string | undefined;
  let OnboardingAppPath: string;
  let getLanguage: () => string;

  let BlogBaseUrl: string;
  let TrainingBaseUrl: string;
  let SubscribeUrl: string;
  let SocialAppPath: string;

  function initialize(routerInstance: ReturnType<typeof createBrowserRouter>, params: IHistoryInitParams) {
    router = routerInstance;
    LoginBaseUrl = params.loginBaseUrl;
    LogoutBaseUrl = params.logoutBaseUrl;
    SignupBaseUrl = params.signupBaseUrl;
    OnboardingAppPath = params.OnboardingAppPath;
    BlogBaseUrl = params.BlogBaseUrl;
    TrainingBaseUrl = params.TrainingBaseUrl;
    SubscribeUrl = params.SubscribeUrl;
    SocialAppPath = params.SocialAppPath;
    getLanguage = params.getLanguageFunction;
    initialized = true;
    router.subscribe((state) => {
      const path = router.createHref(state.location);
      fireListeners(path);
    });
  }

  const isInited = () => {
    return initialized;
  };

  const checkInit = () => {
    if (!isInited()) {
      throw new Error('History service not initialized');
    }
  };

  const stripBasename = (path: string) => {
    if (router.basename && router.basename !== '/' && path.startsWith(router.basename)) {
      path = path.replace(router.basename, '');
    }
    return path;
  };

  const addListener = (listener: (path: string) => void) => {
    checkInit();
    const currentPath = router.createHref(router.state.location);
    listener(currentPath);
    listeners.push(listener);
    return () => {
      removeListener(listener);
    };
  };

  const removeListener = (listener: (path: string) => void) => {
    checkInit();
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };

  const fireListeners = (path: string) => {
    checkInit();
    listeners.forEach((listener) => {
      listener(path);
    });
  };

  const clearListeners = () => {
    checkInit();
    listeners = [];
  };

  const getNormalizedUrl = (url: URL) => {
    let normalizedUrl = url.toString();
    if (normalizedUrl.indexOf('/?') < 0 && normalizedUrl.indexOf('?') >= 0) {
      normalizedUrl = normalizedUrl.replace('?', '/?');
    }
    return normalizedUrl;
  };

  const getExternalUrl = (
    url: string,
    lang?: string,
    session?: string,
    attributionCallback?: (search: URLSearchParams) => void
  ) => {
    // get current origin
    const base = window.location.origin;
    // base will be applied only if url is relative
    const resultUrl = new URL(url, base);

    if (lang) {
      resultUrl.searchParams.set('lang', lang);
    }
    if (session) {
      resultUrl.searchParams.set('session', session);
    }
    if (attributionCallback) attributionCallback(resultUrl.searchParams);
    return getNormalizedUrl(resultUrl);
  };

  const getInternalUrl = (pathname?: string, search?: string) => {
    checkInit();
    const location = { ...router.state.location };
    if (pathname) {
      location.pathname = pathname;
    }
    if (search) {
      location.search = search;
    }
    return router.createHref(location);
  };

  const redirectExternal = (url?: string, lang?: string, session?: string) => {
    if (!url) {
      return;
    }
    const redirectUrl = getExternalUrl(url, lang, session);
    return window.location.assign(redirectUrl);
  };

  const openExternalUrl = (url: string) => {
    if (windowObjectReference === null || windowObjectReference.closed) {
      windowObjectReference = window.open(url, 'NNChildWindow');
    } else if (previousURL !== url) {
      windowObjectReference = window.open(url, 'NNChildWindow');
      if (!windowObjectReference) {
        return;
      }
      windowObjectReference.focus();
    } else {
      windowObjectReference.focus();
    }
    previousURL = url;
  };

  const redirectInternal = (pathname: string, state?: any, search?: string) => {
    checkInit();
    // do not redirect if already on desired page
    if (router.state.location.pathname === pathname) {
      return;
    }
    return router.navigate({ pathname, search }, { state });
  };

  const getBackUrl = (path?: string, search?: string) => {
    checkInit();
    const backUrlPath = path ?? router.state.location.pathname;
    const backUrlSearch = search ?? router.state.location.search;
    const internalHref = getInternalUrl(backUrlPath, backUrlSearch);
    const backUrl = new URL(internalHref, window.location.origin);
    return getNormalizedUrl(backUrl);
  };

  const getCurrentPath = () => {
    checkInit();
    return router.state.location.pathname;
  };

  const isOnPage = (pathname: string) => {
    return getCurrentPath().includes(pathname);
  };

  const getCurrentSearchParams = (searchParams?: string) => {
    checkInit();
    const search = searchParams ?? router.state.location.search;
    return new URLSearchParams(search);
  };

  const updateSearchParams = (newSearch: URLSearchParams) => {
    checkInit();
    const location = router.state.location;
    location.pathname = stripBasename(location.pathname);
    const newSearchQuery = newSearch.toString();
    if (location.search !== newSearchQuery) {
      router.navigate(
        { ...location, pathname: location.pathname, search: newSearchQuery },
        { replace: true, state: location.state }
      );
    }
  };

  /**
   * Function makes redirect to the same location but with updated state
   * it adds new entry to browser history keeping url same
   * @param state state that should be applied to location
   */
  function redirectWithState(state: any) {
    checkInit();
    const currentPath = router.state.location.pathname;
    router.navigate(stripBasename(currentPath), { state });
  }

  const externalRedirect = (url: string, lang?: string, session?: string) => {
    if (!url) {
      return;
    }
    const redirectUrl = getExternalUrl(url, lang, session);
    return router.navigate(redirectUrl);
  };

  const closeExternalUrlTab = () => {
    if (windowObjectReference === null || windowObjectReference.closed) {
      console.log('No external tab is opened!');
    } else {
      console.log('Trying to close external tab...');
      windowObjectReference.close();
    }
  };

  const getSearchParamByName = (param: string) => {
    const searchParams = getCurrentSearchParams();
    const paramValue = searchParams.get(param);
    if (!paramValue) {
      return;
    }
    return paramValue;
  };

  const push = (location: To, state?: any) => {
    checkInit();
    router.navigate(location, { state });
  };

  const getLocation = () => {
    checkInit();
    return router.state.location;
  };

  const setPathName = (name: string) => {
    const location = { ...router.state.location, pathname: name };
    router.navigate(location, { replace: true });
  };

  const setSearch = (name: string) => {
    const location = {
      ...router.state.location,
      pathname: stripBasename(router.state.location.pathname),
      search: name,
    };
    router.navigate(location, { replace: true });
  };

  const redirectToSSO = (backUrlPath?: string, logout?: number, signin?: string, signup = false) => {
    const language = getLanguage();
    const redirectUrl = getSsoRedirectUrl(language, backUrlPath, logout, signin, signup);
    sessionStorage.remove('session');
    return window.location.assign(redirectUrl);
  };

  const redirectToTraining = (language?: string, session?: string) => {
    const trainingUrl = getTrainingUrl('', language, session);
    return window.location.assign(trainingUrl);
  };

  const redirectLogout = (backUrlPath?: string) => {
    return redirectToSSO(backUrlPath || OnboardingAppPath, 1);
  };

  const exchangeAccessToken = (accessToken: string) => {
    const backUrl = getBackUrl('', '?');
    return redirectToSSO(backUrl, 0, accessToken);
  };

  const redirectLogin = (backUrl = '') => {
    return redirectToSSO(backUrl, 0, '');
  };

  const refreshToken = () => {
    const backUrlPath = router.state.location.pathname;
    return redirectLogin(backUrlPath);
  };

  const redirectSignup = (backUrlPath?: string) => {
    return redirectToSSO(backUrlPath, 0, undefined, true);
  };

  const getSsoRedirectUrl = (language?: string, backUrl?: string, logout?: number, signin?: string, signup = false) => {
    // get current origin
    const base = window.location.origin;
    let redirectUrl: URL;
    if (logout) {
      redirectUrl = new URL(LogoutBaseUrl, base);
    } else if (!signup) {
      redirectUrl = new URL(LoginBaseUrl, base);
    } else {
      redirectUrl = new URL(SignupBaseUrl || '', base);
    }
    const resultBackUrl = backUrl ? getExternalUrl(backUrl, language) : getBackUrl();
    redirectUrl.searchParams.set('backUrl', resultBackUrl);
    if (language) {
      redirectUrl.searchParams.set('lang', language);
    }

    if (logout) {
      redirectUrl.searchParams.set('logout', `${logout}`);
    } else if (signin) {
      redirectUrl.searchParams.set('signin', signin);
    }
    return getNormalizedUrl(redirectUrl);
  };

  const getSignupUrl = (backUrl: string, lang?: string) => {
    return getSsoRedirectUrl(lang, backUrl, undefined, undefined, true);
  };

  const getBlogUrl = (lang: string) => `${BlogBaseUrl}/${lang}`;

  const getEffectivenessUrl = (lang: string) => {
    const blogUrl = getBlogUrl(lang);
    let path = '';
    switch (lang) {
      case 'en':
        path = '/bulletproof-brain-training-study-proves-effectiveness-of-neuronation-premium/';
        break;
      case 'de':
        path = '/neue-studie-beweist-neuronation-mit-hoher-signifikanz-effektiv/';
        break;
      case 'fr':
        path = '/resultats-detude-neuronation-ameliore-la-memoire/';
        break;
      case 'ru':
        path =
          '/%d1%80%d0%b5%d0%b7%d1%83%d0%bb%d1%8c%d1%82%d0%b0%d1%82%d1%8b-%d0%b8%d1%81%d1%81%d0%bb%d0%b5%d0%b4%d0%be%d0%b2%d0%b0%d0%bd%d0%b8%d1%8f-neuronation-%d1%83%d0%bb%d1%83%d1%87%d1%88%d0%b0%d0%b5%d1%82/';
        break;
    }
    return getExternalUrl(`${blogUrl}${path}`);
  };

  const getGroupPackageUrl = () => {
    const groupPackageUrl = getExternalUrl(`${OnboardingAppPath}/lp/corp`);
    return groupPackageUrl.toString();
  };

  const getJobsUrl = () => {
    const jobsUrl = new URL('https://neuronation.breezy.hr/');
    return jobsUrl.toString();
  };

  const getTrainingUrl = (subpage = '', language?: string, session?: string) =>
    getExternalUrl(`${TrainingBaseUrl}${subpage}`, language, session);

  const buildSubscribeSubpageLink = (subpage = '', language?: string, sessionToken?: string) =>
    getExternalUrl(`${SubscribeUrl}${subpage}`, language, sessionToken);

  const buildSubscribePageLink = (language?: string, sessionToken?: string) =>
    buildSubscribeSubpageLink('', language, sessionToken);

  const getSocialUrl = (subpage = '', language?: string, session?: string) =>
    getExternalUrl(`${SocialAppPath}${subpage}`, language, session);

  const getFriendsUrl = (language?: string, session?: string) => getSocialUrl('/friends', language, session);

  const getMessagesUrl = (language?: string, session?: string) => getSocialUrl('/messages', language, session);

  function addBasicSearchParams(url: URL, language?: string, session?: string, trackingParams = false): void {
    const currentUrl = new URL(window && window.location.href);
    // remove session token from current url, it will be replaced with fresh one if needed
    url.searchParams.delete('signin');
    url.searchParams.delete('session');

    if (trackingParams) {
      // forward any tracking parameters
      const trackings = ['utm_campaign', 'utm_source', 'utm_medium'];
      currentUrl.searchParams.forEach((value, key) => {
        if (trackings.includes(key)) {
          url.searchParams.append(key, value);
        }
      });
    }

    // add language param
    if (language) {
      url.searchParams.set('lang', language);
    }

    // add session token if user is authorized
    if (session) {
      url.searchParams.set('session', session);
    }
  }

  const memoOrdersUrl = () => {
    let cache = '';
    return (session?: string, language?: string) => {
      if (cache) {
        return cache;
      } else {
        const subscribeUrlString = buildSubscribeSubpageLink('/orders');
        const subscribeOrdersUrl = new URL(subscribeUrlString);
        addBasicSearchParams(subscribeOrdersUrl, language, session, true);
        cache = subscribeOrdersUrl.toString();
        return cache;
      }
    };
  };

  const getOrdersUrl = memoOrdersUrl();

  const memoSubscriptionManageUrl = () => {
    let cache = '';
    return (session?: string, language?: string) => {
      if (cache) {
        return cache;
      } else {
        const subscribeUrlString = buildSubscribeSubpageLink('/manage');
        const subscribeManageUrl = new URL(subscribeUrlString);
        addBasicSearchParams(subscribeManageUrl, language, session, true);
        cache = subscribeManageUrl.toString();
        return cache;
      }
    };
  };

  const getSubscriptionManageUrl = memoSubscriptionManageUrl();

  return {
    initialize,
    isInited,
    addListener,
    removeListener,
    clearListeners,
    getNormalizedUrl,
    getInternalUrl,
    getExternalUrl,
    redirectExternal,
    openExternalUrl,
    redirectInternal,
    getBackUrl,
    getCurrentPath,
    isOnPage,
    getCurrentSearchParams,
    updateSearchParams,
    redirectWithState,
    externalRedirect,
    closeExternalUrlTab,
    getSearchParamByName,
    push,
    getLocation,
    setPathName,
    setSearch,
    redirectLogout,
    exchangeAccessToken,
    redirectLogin,
    redirectSignup,
    refreshToken,
    getSsoRedirectUrl,
    getSignupUrl,
    redirectToSSO,
    redirectToTraining,
    getBlogUrl,
    getEffectivenessUrl,
    getGroupPackageUrl,
    getJobsUrl,
    getTrainingUrl,
    buildSubscribeSubpageLink,
    buildSubscribePageLink,
    getSocialUrl,
    getFriendsUrl,
    getMessagesUrl,
    getSubscriptionManageUrl,
    getOrdersUrl,
  };
}

const newHistoryService = createHistoryService();
export default newHistoryService;
